/**
 * Global billing-bypass gate.
 *
 * Warrior Trading launch blocker P0#2: a stray `VITE_BILLING_BYPASS=true` must
 * never be able to grant free access in production. Billing bypass is a
 * LOCAL-DEVELOPMENT convenience ONLY.
 *
 * This single helper centralises the decision so it cannot be re-implemented
 * inconsistently (and accidentally enabled) elsewhere:
 *
 *   - Enabled ONLY when running a Vite DEV build (`import.meta.env.DEV === true`)
 *     AND the flag is literally "true".
 *   - Hard-disabled on ANY production signal, even if the flag is left on:
 *       * `import.meta.env.PROD === true`
 *       * `import.meta.env.MODE === "production"`
 *       * Vercel's injected `VITE_VERCEL_ENV === "production"`
 *
 * The default environment is read from `import.meta.env` at call time, but a
 * caller may inject its own env record for deterministic unit testing.
 */

export type BillingBypassEnv = {
  DEV?: boolean;
  PROD?: boolean;
  MODE?: string;
  VITE_BILLING_BYPASS?: string;
  VITE_VERCEL_ENV?: string;
};

function readViteImportMetaEnv(): BillingBypassEnv {
  const env = (import.meta as unknown as { env?: BillingBypassEnv }).env;
  return env ?? {};
}

function isTrue(value?: string | boolean): boolean {
  return String(value ?? '')
    .trim()
    .toLowerCase() === 'true';
}

export function isGlobalBillingBypassEnabled(env: BillingBypassEnv = readViteImportMetaEnv()): boolean {
  if (!env) return false;

  const runningDevBuild = env.DEV === true;
  const anyProductionSignal =
    env.PROD === true ||
    (env.MODE != null && String(env.MODE).toLowerCase() === 'production') ||
    (env.VITE_VERCEL_ENV != null && String(env.VITE_VERCEL_ENV).toLowerCase() === 'production');

  // Hard block: bypass must never be possible outside a local dev build, even
  // if the env var is accidentally left enabled.
  if (!runningDevBuild || anyProductionSignal) {
    return false;
  }

  // Dev-only convenience: requires the explicit opt-in flag.
  return isTrue(env.VITE_BILLING_BYPASS);
}

export const BILLING_BYPASS_ENABLED: boolean = isGlobalBillingBypassEnabled();
